// src/prototype/components/explorerColumns.ts
// ============================================================================
// USE CASE EXPLORER — ONE COLUMN DEFINITION, SHARED BY EVERY ROW.
//
// The header, the unlocked row (ExplorerRow) and the freemium locked row
// (EALockedOpportunityRow) each used to declare their own widths. Two of the six
// columns had a width in the header and a different one — or none — in the row:
// "Est. value" was `hidden sm:block` in the header against `text-right shrink-0`
// in the row, and "Compare · Open" was an unsized `shrink-0` against a flex group
// whose width changed with the buttons inside it. So the headers sat wherever
// their own text width put them, and the only way to line anything up was to
// nudge padding on one side until it looked right at one viewport — which is
// exactly what breaks at the next one.
//
// Every column is now declared once here and consumed by all three. Changing a
// width means changing it in one place, and a header can no longer disagree with
// the cell beneath it.
//
// RESPONSIVE VISIBILITY IS PART OF THE COLUMN. The `hidden sm:/md:/lg:` classes
// live in these strings rather than at the call sites, so a column cannot be
// visible in the header at a breakpoint where its cell is hidden (or the
// reverse) — the previous arrangement made that mismatch a one-word edit away.
// ============================================================================

/**
 * Column classes. Apply verbatim to the header cell and to the matching cell in
 * every row variant; add only content-level classes (typography, colour) on top.
 */
export const EXPLORER_COLS = {
  /** Outer row/header container: the flex track the columns sit on. */
  track: 'flex items-center gap-4 px-5',
  /** Rank badge. */
  rank: 'w-9 shrink-0',
  /** Title + verdict + why-surfaced. The only elastic column. */
  title: 'flex-1 min-w-0',
  /** Est. value — right-aligned so the figures form a column, not a ragged edge. */
  value: 'w-32 shrink-0 text-right hidden sm:block',
  /** Time to value. */
  ttv: 'w-24 shrink-0 hidden md:block',
  /** Effort tag. */
  effort: 'w-20 shrink-0 hidden lg:block',
  /**
   * Compare · Open. Fixed width and right-aligned: its contents vary by row
   * (portfolio button present or not, checkbox present or not, upgrade CTA on a
   * locked row), and an unsized column would let each row end somewhere else.
   */
  actions: 'w-[212px] shrink-0 flex items-center justify-end gap-3',
} as const;

/**
 * Spacer cells a row must render to keep later columns on the grid when it has
 * nothing to show in the middle of the track — the freemium locked row, which
 * carries no value/TTV/effort but must still land its CTA in the actions column.
 */
export const EXPLORER_SPACERS = [
  EXPLORER_COLS.value,
  EXPLORER_COLS.ttv,
  EXPLORER_COLS.effort,
] as const;
